import equal from "fast-deep-equal/es6";
import { runInAction } from "mobx";

import { DeviceClient } from "@formant/core/client/DeviceClient";
import { defined } from "@formant/core/common/defined";
import { delay } from "@formant/core/common/delay";
import { randomString } from "@formant/core/common/randomString";
import {
    IPromiseIntervalHandle,
    setPromiseInterval,
} from "@formant/core/common/setPromiseInterval";
import { ICommandStore } from "@formant/core/model/data/ICommandStore";
import { IDeviceStore } from "@formant/core/model/data/IDeviceStore";
import { duration } from "@formant/core/model/duration";
import { IAdapterConfiguration } from "@formant/core/model/IAdapterConfiguration";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceQuery } from "@formant/core/model/IDeviceQuery";
import { IDeviceStreamConfiguration } from "@formant/core/model/IDeviceStreamConfiguration";
import { IDeviceStreamRosLocalizationConfiguration } from "@formant/core/model/IDeviceStreamRosLocalizationConfiguration";
import { IDeviceStreamRosTopicConfiguration } from "@formant/core/model/IDeviceStreamRosTopicConfiguration";
import { IDeviceStreamRosTransformTreeConfiguration } from "@formant/core/model/IDeviceStreamRosTransformTreeConfiguration";
import { IDeviceTelemetryConfiguration } from "@formant/core/model/IDeviceTelemetryConfiguration";
import { IDeviceTeleopConfiguration } from "@formant/core/model/IDeviceTeleopConfiguration";
import { IDeviceTeleopHardwareStreamConfiguration } from "@formant/core/model/IDeviceTeleopHardwareStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { ITags } from "@formant/core/model/ITags";
import { ITagSets } from "@formant/core/model/ITagSets";
import { ITeleopJoystickConfiguration } from "@formant/core/model/ITeleopJoystickConfiguration";
import { ITeleopViewConfiguration } from "@formant/core/model/ITeleopViewConfiguration";
import { defaultROSVersion } from "@formant/core/model/rosVersions";
import { Uuid } from "@formant/core/model/Uuid";

import { adapterClient } from "../clients";
import { cleanHardwareName } from "../utils/cleanHardwareName";
import { hashObservable } from "../utils/hashObservable";
import { rosTopicToStreamName } from "../utils/rosTopicToStreamName";
import { IAuthStore } from "./IAuthStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { StoreCache } from "./StoreCache";

const countTTL = 5 * duration.second;

export class DeviceStore implements IDeviceStore {
    @hashObservable()
    public issuingCommand: boolean = false;

    private listCache = new StoreCache<IDeviceQuery | undefined, IDevice[]>();
    private cache = new StoreCache<Uuid, IDevice | false>();
    private tagsCache = new StoreCache<IDeviceQuery | undefined, ITagSets>();
    private countCache = new StoreCache<IDeviceQuery, number>({
        staleIntervalMs: countTTL,
        isEqual: equal,
    });
    private focusedDevice: string | undefined;
    private focusedDeviceUpdateTimerHandle: IPromiseIntervalHandle | undefined;
    private focusedDeviceCommandCheckTimerHandle:
        | IPromiseIntervalHandle
        | undefined;
    private onDemandRequestCommandCount: Map<Uuid, number> = new Map();

    constructor(
        private deviceClient: DeviceClient,
        private authStore: IAuthStore,
        private commandStore: ICommandStore,
        private deviceDetailsStore: IDeviceDetailsStore,
    ) {}

    list(query?: IDeviceQuery): IDevice[] | undefined {
        return this.listCache.get(
            query,
            async () => await this.fetchList(query),
        );
    }

    get(id: Uuid): IDevice | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    getAsync(id: Uuid): Promise<IDevice | false> {
        return this.cache.getAsync(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    tags(query?: IDeviceQuery): ITagSets | undefined {
        return this.tagsCache.get(
            query,
            async () =>
                await this.deviceClient.getTags(
                    await this.authStore.token(),
                    query,
                ),
        );
    }

    tagsAsync(query?: IDeviceQuery): Promise<ITagSets> {
        return this.tagsCache.getAsync(
            query,
            async () =>
                await this.deviceClient.getTags(
                    await this.authStore.token(),
                    query,
                ),
        );
    }

    async fetchGet(id: Uuid): Promise<IDevice | false> {
        const device = await this.deviceClient.get(
            await this.authStore.token(),
            id,
        );
        const value = device || false;
        this.cache.set(id, value);
        return value;
    }

    async create(device: IDevice): Promise<IDevice> {
        const created = await this.deviceClient.create(
            await this.authStore.token(),
            device,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        this.countCache.clear();
        this.deviceDetailsStore.clear();
        return created;
    }

    async createWithDefaults(
        name?: string,
        tags?: ITags,
        customTemplate?: IDeviceConfigurationDocument,
    ): Promise<IDevice> {
        const device = await this.create({
            name: name || `device-${randomString(4)}`,
            tags: tags || {},
            publicKey: "",
        });
        const id = defined(device.id);
        const emptyDeviceConfiguration: IDeviceConfigurationDocument = {};
        const template = customTemplate || emptyDeviceConfiguration;

        await this.deviceClient.updateConfiguration(
            await this.authStore.token(),
            id,
            {
                deviceId: id,
                document: {
                    ...template,
                    tags,
                },
            },
        );
        return defined((await this.fetchGet(id)) || undefined);
    }

    async waitForConfiguration(deviceId: string, allowAny: boolean = false) {
        while (true) {
            await delay(duration.second);
            const device = await this.fetchGet(deviceId);
            if (!device) {
                return;
            }
            const { state } = device;
            if (
                allowAny ||
                state?.reportedConfiguration?.version ===
                    device.desiredConfigurationVersion
            ) {
                return;
            }
        }
    }

    async autoConfigureDevice(
        device: IDevice,
    ): Promise<IDeviceConfigurationDocument | false> {
        const { tags, state } = device;
        const topics = state?.ros?.topics || [];

        const topicsByType = (type: string) =>
            topics.filter(_ => _.type === type).map(_ => _.name);

        const baseLocalizationReferenceFrame = "map";
        const baseTfReferenceFrame = "base_link";

        const imageTopics = topicsByType("sensor_msgs/CompressedImage").slice(
            0,
            2,
        );
        const pointCloudTopics = [
            ...topicsByType("sensor_msgs/LaserScan"),
            ...topicsByType("sensor_msgs/PointCloud2"),
        ].slice(0, 2);
        const logTopics = topicsByType("rosgraph_msgs/Log").slice(0, 1);
        const diagnosticArrayTopics = topicsByType(
            "diagnostic_msgs/DiagnosticArray",
        ).slice(0, 2);
        const navSatFixTopics = topicsByType("sensor_msgs/NavSatFix").slice(
            0,
            2,
        );
        const batteryStateTopics = topicsByType(
            "sensor_msgs/BatteryState",
        ).slice(0, 2);

        const rosTopicStreams = [
            ...imageTopics,
            ...pointCloudTopics,
            ...logTopics,
            ...diagnosticArrayTopics,
            ...navSatFixTopics,
            ...batteryStateTopics,
        ].map<IDeviceStreamConfiguration<IDeviceStreamRosTopicConfiguration>>(
            topic => ({
                name: rosTopicToStreamName(topic),
                configuration: {
                    type: "ros-topic",
                    topic,
                    rosVersion: defaultROSVersion,
                },
            }),
        );

        const [mapTopic] = topicsByType("nav_msgs/OccupancyGrid");
        const [odomTopic] = topicsByType("nav_msgs/Odometry");
        const [pathTopic] = topicsByType("nav_msgs/Path");
        // TODO: remove for now until we can figure out how to handle this
        const localizationStream:
            | IDeviceStreamConfiguration<IDeviceStreamRosLocalizationConfiguration>
            | undefined =
            mapTopic && odomTopic
                ? {
                      name: "localization",
                      configuration: {
                          type: "ros-localization",
                          baseReferenceFrame: baseLocalizationReferenceFrame,
                          odomTopic,
                          mapTopic,
                          pathTopic,
                          pointCloudTopics:
                              pointCloudTopics.length > 0
                                  ? pointCloudTopics
                                  : undefined,
                          rosVersion: defaultROSVersion,
                      },
                  }
                : undefined;

        const tfTopics = topicsByType("tf2_msgs/TFMessage");
        const transformTreeStream:
            | IDeviceStreamConfiguration<IDeviceStreamRosTransformTreeConfiguration>
            | undefined =
            tfTopics.includes("/tf") || tfTopics.includes("/tf_static")
                ? {
                      name: "transform_tree",
                      configuration: {
                          type: "ros-transform-tree",
                          baseReferenceFrame: baseTfReferenceFrame,
                          rosVersion: defaultROSVersion,
                      },
                  }
                : undefined;

        const autoConfiguredTelemetry: IDeviceTelemetryConfiguration = {
            streams: [
                ...rosTopicStreams,
                ...(localizationStream ? [localizationStream] : []),
                ...(transformTreeStream ? [transformTreeStream] : []),
            ],
        };

        const getTeleopAutoTopicName = (config: {
            type: string;
            preferred?: string[];
            avoid?: string[];
            highToLowPriorityKeywords: string[];
            excludeTopicNames?: string[];
        }) => {
            const {
                type,
                highToLowPriorityKeywords,
                preferred,
                avoid,
                excludeTopicNames,
            } = config;

            // Candidates are any topics with the correct type that exclude the given topic names
            const candidates = topicsByType(type).filter(
                topicName =>
                    !(excludeTopicNames || []).some(name => topicName === name),
            );

            // Preferred candidates include preferred strings and exclude avoid strings
            const preferredCandidates = candidates
                .filter(topicName =>
                    (preferred || []).some(_ => topicName.includes(_)),
                )
                .filter(
                    topicName =>
                        !(avoid || []).some(_ => topicName.includes(_)),
                );

            // The preferred match is a topic name that matches the highest ranked keyword,
            // chosen from the pool of preferred candidate topic names, or undefined
            const preferredMatch = preferredCandidates
                .filter(topicName =>
                    highToLowPriorityKeywords.some(_ => topicName.includes(_)),
                )
                .find(_ => true);

            // The any match is a topic name that mathces the highest priority keyword,
            // chosen from the pool of all candidate topic names, or undefined
            const anyMatch = candidates
                .filter(topicName =>
                    highToLowPriorityKeywords.some(_ => topicName.includes(_)),
                )
                .find(_ => true);

            const preferredCandidate = preferredCandidates.find(
                _ => _ !== undefined,
            );
            const anyCandidate = candidates.find(_ => _ !== undefined);

            return (
                preferredMatch ||
                anyMatch ||
                preferredCandidate ||
                anyCandidate ||
                undefined
            );
        };

        // Auto-configure up to two teleop image streams and one velocity stream

        const teleopPrimaryImageTopicName: string | undefined =
            getTeleopAutoTopicName({
                type: "sensor_msgs/Image",
                highToLowPriorityKeywords: [
                    "front",
                    "forward",
                    "primary",
                    "main",
                ],
                preferred: ["rgb", "cam", "color"],
                avoid: ["depth", "map"],
            });
        const teleopPrimaryImageTopic:
            | IDeviceTeleopRosStreamConfiguration
            | undefined = teleopPrimaryImageTopicName
            ? {
                  topicName: teleopPrimaryImageTopicName,
                  topicType: "formant/H264VideoFrame",
                  mode: "observe",
              }
            : undefined;

        const teleopSecondaryImageTopicName: string | undefined =
            getTeleopAutoTopicName({
                type: "sensor_msgs/Image",
                highToLowPriorityKeywords: [
                    "rear",
                    "back",
                    "reverse",
                    "secondary",
                    "side",
                    "left",
                    "right",
                ],
                preferred: ["rgb", "cam", "color"],
                excludeTopicNames: [teleopPrimaryImageTopicName || ""],
            });
        const teleopSecondaryImageTopic:
            | IDeviceTeleopRosStreamConfiguration
            | undefined = teleopSecondaryImageTopicName
            ? {
                  topicName: teleopSecondaryImageTopicName,
                  topicType: "formant/H264VideoFrame",
                  mode: "observe",
              }
            : undefined;

        const teleopVelocityTopicName: string | undefined =
            getTeleopAutoTopicName({
                type: "geometry_msgs/Twist",
                highToLowPriorityKeywords: ["teleop", "cmd_vel", "joy", "game"],
            });
        const teleopVelocityTopic:
            | IDeviceTeleopRosStreamConfiguration
            | undefined = teleopVelocityTopicName
            ? {
                  topicName: teleopVelocityTopicName,
                  topicType: "geometry_msgs/Twist",
                  mode: "command",
              }
            : undefined;

        const teleopRosStreams: IDeviceTeleopRosStreamConfiguration[] = [
            teleopPrimaryImageTopic,
            teleopSecondaryImageTopic,
            teleopVelocityTopic,
        ]
            .filter(_ => _)
            .map(_ => defined(_));

        // Default joystick configuration for simple ground robots
        const defaultTeleopJoystickConfiguration: ITeleopJoystickConfiguration =
            {
                position: "right",
                x: {
                    dimension: "angular-z",
                    gamepadAxis: 2,
                    scale: -1,
                    expo: 2,
                },
                y: {
                    dimension: "linear-x",
                    gamepadAxis: 3,
                    scale: 1,
                    expo: 2,
                },
            };
        const teleopJoysticks: ITeleopJoystickConfiguration[] | undefined =
            teleopVelocityTopic
                ? [defaultTeleopJoystickConfiguration]
                : undefined;

        const shouldConfigureTeleopView =
            teleopPrimaryImageTopicName ||
            teleopSecondaryImageTopicName ||
            localizationStream;
        let teleopViewConfigurations:
            | [
                  ITeleopViewConfiguration,
                  ITeleopViewConfiguration,
                  ITeleopViewConfiguration,
                  ITeleopViewConfiguration,
                  ITeleopViewConfiguration,
              ]
            | undefined = shouldConfigureTeleopView
            ? [
                  { streamName: teleopPrimaryImageTopicName },
                  {},
                  { streamName: teleopSecondaryImageTopicName },
                  {
                      streamName: localizationStream && localizationStream.name,
                  },
                  {},
              ]
            : undefined;

        let teleopHardwareStreams: IDeviceTeleopHardwareStreamConfiguration[] =
            [];

        // If we didn't find a ROS topic, we have hardware video devices
        // lets setup a hardware video
        if (
            teleopPrimaryImageTopicName === undefined &&
            device?.state?.hwInfo?.videoDevices &&
            device.state.hwInfo.videoDevices.length > 0
        ) {
            const videoDevices = device.state.hwInfo.videoDevices;
            // As per Aaron, deprioritize IR
            videoDevices.sort((a, b) => {
                if (a.name.includes("IR")) {
                    return 1;
                }
                if (b.name.includes("IR")) {
                    return -1;
                }
                return 0;
            });
            let videoDevice = videoDevices[0];

            // RealSense is very common
            const realSenseVideos = videoDevices.filter(x =>
                x.name.includes("RealSense"),
            );
            if (realSenseVideos.length === 3) {
                // As per Aaron, realsense often come in three, and the third is the one we want
                videoDevice = realSenseVideos[2];
            } else if (realSenseVideos.length > 0) {
                // prefer RealSense even if there's other number of devices
                videoDevice = realSenseVideos[0];
            }

            const name = cleanHardwareName(
                `${videoDevice.name} - ${videoDevice.path}`,
            );
            teleopHardwareStreams = [
                {
                    hwDescriptor: videoDevice.path,
                    mode: "observe",
                    hardwareType: "usb",
                    name,
                    quality: "720p",
                    rtcStreamType: "h264-video-frame",
                },
            ];

            const hwTeleopConfig = {
                streamName: name,
            };
            if (teleopViewConfigurations) {
                teleopViewConfigurations[0] = hwTeleopConfig;
            } else {
                teleopViewConfigurations = [hwTeleopConfig, {}, {}, {}, {}];
            }
        }

        const autoConfiguredTeleop: IDeviceTeleopConfiguration = {
            rosStreams: teleopRosStreams,
            hardwareStreams: teleopHardwareStreams,
            joysticks: teleopJoysticks,
            views: teleopViewConfigurations,
        };

        // todo: Once we fix grpcio from being slow, add these back
        const selfServeAdapters: string[] = [];
        // const selfServeAdapters = ["default-adapter", "ros-service-adapter"];

        const autoConfiguredAdapters: IAdapterConfiguration[] =
            (await adapterClient.list(await this.authStore.token()))
                ?.filter(_ => selfServeAdapters.includes(_.name))
                .filter(_ => _.id)
                .map(_ => ({
                    id: defined(_.id),
                    name: _.name,
                    fileId: _.fileId,
                    execCommand: _.execCommand,
                })) || [];

        const document: IDeviceConfigurationDocument = {
            tags,
            adapters: autoConfiguredAdapters,
            teleop: autoConfiguredTeleop,
            telemetry: autoConfiguredTelemetry,
        };

        return document;
    }

    async update(id: Uuid, device: Partial<IDevice>) {
        const updated = await this.deviceClient.update(
            await this.authStore.token(),
            id,
            device,
        );
        runInAction(() => {
            this.cache.set(id, updated);
            this.countCache.clear();
            this.tagsCache.clear();
            this.deviceDetailsStore.clear();
        });
        return updated;
    }

    async disable(id: Uuid) {
        const updated = await this.deviceClient.disable(
            await this.authStore.token(),
            id,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        this.countCache.clear();
        this.deviceDetailsStore.clear();
    }

    async fetchList(query?: IDeviceQuery): Promise<IDevice[]> {
        const token = await this.authStore.token();
        const value = await this.deviceClient.list(token, query);

        runInAction(() => {
            this.listCache.set(query, value);
            value.forEach(_ => this.cache.set(defined(_.id), _));
        });

        return value;
    }

    refreshList(query?: IDeviceQuery) {
        return this.listCache.refresh(query, () => this.fetchList(query), {
            lazy: true,
        });
    }

    count(query: IDeviceQuery): number | undefined {
        return this.countCache.get(query, async () =>
            this.deviceClient.count(await this.authStore.token(), query),
        );
    }

    clear() {
        this.tagsCache.clear();
        this.cache.clear();
        this.tagsCache.clear();
        this.countCache.clear();
    }

    setFocusedDevice(deviceId: string) {
        const { commandStore } = this;

        if (this.focusedDevice) {
            this.unsetFocusedDevice();
        }
        this.focusedDevice = deviceId;

        // update every minute
        this.focusedDeviceUpdateTimerHandle = setPromiseInterval({
            func: async () => {
                if (this.onDemandRequestCommandCount && this.focusedDevice) {
                    await this.fetchGet(this.focusedDevice);
                }
            },
            delay: duration.minute,
        });
        // let's refresh presence if on demand request command counts change
        this.focusedDeviceCommandCheckTimerHandle = setPromiseInterval({
            func: async () => {
                if (this.focusedDevice && this.focusedDevice) {
                    const oldCount = this.onDemandRequestCommandCount.get(
                        this.focusedDevice,
                    );
                    const newCount = commandStore.getCurrentOnDemandRequests(
                        this.focusedDevice,
                    ).length;
                    if (oldCount !== undefined && oldCount !== newCount) {
                        await this.fetchGet(this.focusedDevice);
                    }
                    this.onDemandRequestCommandCount.set(
                        this.focusedDevice,
                        newCount,
                    );
                }
            },
            delay: duration.second * 10,
        });
    }

    unsetFocusedDevice() {
        this.focusedDevice = undefined;
        if (this.focusedDeviceUpdateTimerHandle !== undefined) {
            this.focusedDeviceUpdateTimerHandle.stop().catch(e => {
                throw e;
            });
            this.focusedDeviceUpdateTimerHandle = undefined;
        }
        if (this.focusedDeviceCommandCheckTimerHandle !== undefined) {
            this.focusedDeviceCommandCheckTimerHandle.stop().catch(e => {
                throw e;
            });
            this.focusedDeviceCommandCheckTimerHandle = undefined;
        }
    }
}
